import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import { InternalPageLink } from '@doltech/routeDefinition';
import { LAYER } from '@doltech/utils/lib/constants';
import { scrollToElementId, scrollToTop } from '@doltech/utils/lib/scrollUtils';
import { verticalSpace } from '@doltech/utils/lib/css-style';
import { getLandingUrlFromPath } from '@doltech/utils/lib/url';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { useAppScrollRef } from '@doltech/core/lib/appScroll/withAppScroll';

import { useScrollTracking } from '@doltech/ui/lib/hooks/useScrollTracking';
import { useOnScreen } from '@doltech/ui/lib/hooks/useOnScreen';
import { dolDinhLucAuthor } from '@doltech/ui/lib/figma/Blog/dolDinhLucAuthor.data';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { LineDivider } from '@doltech/ui/lib/figma/Divider/LineDivider';

import { ElementTableOfContentHeading } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/ElementTableOfContentHeading/ElementTableOfContentHeading';
import { BreadcrumbGroupLayout } from '@doltech/ui/lib/figma/Breadcrumb/BreadcrumbGroupLayout/BreadcrumbGroupLayout';
import { BreadcrumbItem } from '@doltech/ui/lib/figma/Breadcrumb/BreadcrumbItem/BreadcrumbItem';
import { BlogPageElementMetadata } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogPageElementMetadata/BlogPageElementMetadata';
import { BlogEditorElementDividerIndicator } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogEditorElementDividerIndicator/BlogEditorElementDividerIndicator';
import { BlogPageLayoutBody } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogPageLayoutBody/BlogPageLayoutBody';
import { BlogPageElementHeading } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogPageElementHeading/BlogPageElementHeading';
import { BlogPageElementCover } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogPageElementCover/BlogPageElementCover';
import { BlogPageElementBody } from '@doltech/ui/lib/figma/BlogEditorBlogSystem/BlogPageElementBody/BlogPageElementBody';
import { BlogPromotion } from '@doltech/ui/lib/figma/BlogPromotion/BlogPromotion';
import { BlogPromotionPopover } from '@doltech/ui/lib/figma/BlogPromotionPopover/BlogPromotionPopover';

import { usePageObjectContent } from '../admin-blog-management/hook/usePageObjectContent.hook';
import { BlogRelatedPosts } from '../admin-blog-management/book-review/Steps/BlogEditor/BlogRelatedPosts';
import { mapEditorValueWithHeadingIndex } from './utils/heading-mapping';
import { convertTocFromEditorToc } from './utils/toc-mapping';
import { LandingBlogFloatingActionTocPopoverLayout } from './ui/LandingBlogFloatingActionTocPopoverLayout/LandingBlogFloatingActionTocPopoverLayout';
import { LandingSideInfoTableOfContent } from './ui/LandingSideInfoTableOfContent/LandingSideInfoTableOfContent';
import { LandingSideInfoTableOfContentPopover } from './ui/LandingSideInfoTableOfContentPopover/LandingSideInfoTableOfContentPopover';

const FloatingAction = styled.div`
  position: fixed;
  right: 40px;
  bottom: 20px;

  opacity: 0;
  z-index: -1;
  transition: all 0.01s ease-out;

  &.visible {
    opacity: 1;
    z-index: ${LAYER.LAYER10};
  }

  ${fromScreen(1240)} {
    display: none;
  }
`;

const Sidebar = styled.div`
  display: none;

  opacity: 0;
  z-index: -1;
  pointer-events: none;
  transition: all 0.15s ease-out;

  &.visible {
    opacity: 1;
    z-index: 1;
    pointer-events: auto;
  }

  ${fromScreen(1240)} {
    display: block;
  }
`;

const InlineTableOfContent = styled.div`
  ${fromScreen(1240)} {
    display: none;
  }
`;

const BlogContentTopSection = styled.div`
  ${verticalSpace(32)}
`;


const BlogContentLayout = styled.div`
  ${verticalSpace(32)}
`;

const FooterContent = styled.div``;

const BodyContent = styled.div`
  padding: 40px 24px 32px;
`;

const Container = styled.article``;

interface BlogLandingDetailProps {
  detailData?: any;
  seoData?: any;
  relatedPosts?: any;
  internalPageLink?: InternalPageLink;
}

export const BlogLandingDetail = (props: BlogLandingDetailProps) => {
  const { detailData, seoData, relatedPosts, internalPageLink } = props;
  const blogContentRef = React.useRef<HTMLDivElement>(null);
  const blogTopSectionRef = React.useRef<HTMLDivElement>(null);
  const footerContentRef = React.useRef<HTMLDivElement>(null);
  const { isOnScreen: isBlogContentOnScreen } = useOnScreen(blogContentRef);
  const { isOnScreen: isBlogTopSectionOnScreen } = useOnScreen(blogTopSectionRef);
  const { isOnScreen: isFooterContentOnScreen } = useOnScreen(footerContentRef);
  const { appScrollRef } = useAppScrollRef();

  const pageSeoInfo = usePageObjectContent(seoData.type, internalPageLink);
  const bodyValue = mapEditorValueWithHeadingIndex(detailData?.body, ['h2', 'h3', 'h4']);
  const tocContent = convertTocFromEditorToc(detailData?.tableOfContent, detailData?.tableOfContentHidden);

  const sectionIds = tocContent?.map((eachContent) => `section-${eachContent.headingIndex + 1}`);

  const breadcrumbs = [
    {
      key: 'HOME',
      render: () => <BreadcrumbItem type="home" colorStyle="primary" actionType="link" href="/" />,
    },
    {
      key: 'LEVEL_2',
      render: () => (
        <BreadcrumbItem
          type="text"
          colorStyle="primary"
          actionType="link"
          text="Kiến thức IELTS"
          href={internalPageLink?.staticPageLink?.LANDING_IELTS_KNOWLEDGE?.url || '/'}
        />
      )
    },
    {
      key: 'LEVEL_3',
      render: () => (
        <BreadcrumbItem
          type="text"
          colorStyle="primary"
          actionType="link"
          text={pageSeoInfo?.title}
          href={pageSeoInfo?.url}
        />
      )
    },
    {
      key: 'MAIN_TITLE',
      render: () => (
        <BreadcrumbItem
          type="text"
          colorStyle="primary"
          actionType="link"
          text={detailData?.title}
          active
        />
      )
    }
  ];

  const { activeElementId } = useScrollTracking({
    containerEl: appScrollRef.current as HTMLElement,
    elementIds: sectionIds,
    disable: isBlogTopSectionOnScreen || isFooterContentOnScreen
  });

  React.useEffect(() => {
    const currentId = window.location.hash.substring(1);
    const currentEl = document.getElementById(currentId);

    if (currentEl) {
      setTimeout(() => {
        const containerEl = appScrollRef.current as HTMLElement;
        scrollToElementId(currentId, containerEl);
      }, 1000);
    }
  }, [appScrollRef]);

  return (
    <Container className="blog-ielts-landing-detail">
      <BodyContent ref={blogContentRef}>
        <BlogPageLayoutBody
          leftSidebar={
            <Sidebar className={cl('sidebar', { visible: !isBlogTopSectionOnScreen && !isFooterContentOnScreen })}>
              <BlogPromotion />
            </Sidebar>
          }
          rightSidebar={
            detailData?.isTocShown && !isFooterContentOnScreen && (
              <Sidebar className={cl('sidebar', { visible: !isBlogTopSectionOnScreen && !isFooterContentOnScreen })}>
                <LandingSideInfoTableOfContent
                  title="Table of content"
                  content={tocContent.map((eachContent) => {
                    const sectionId = `section-${eachContent.headingIndex + 1}`;

                    return ({
                      id: eachContent.id,
                      render: () => (
                        <ElementTableOfContentHeading
                          type={eachContent.type}
                          order={eachContent.orderIndex + 1}
                          text={eachContent.text}
                          onClick={() => {
                            const containerEl = appScrollRef.current as HTMLElement;
                            scrollToElementId(sectionId, containerEl);
                            window.history.replaceState?.(null, null, `#${sectionId}`);
                          }}
                          active={activeElementId && activeElementId === sectionId}
                          withOrder
                        />
                      )
                    });
                  })}
                />
              </Sidebar>
            )
          }
        >
          <BlogContentLayout>
            <BlogContentTopSection ref={blogTopSectionRef}>
              <BlogPageElementHeading
                headingValue={detailData?.header}
                breadcrumb={<BreadcrumbGroupLayout breadcrumbs={breadcrumbs} />}
              />
              <BlogPageElementMetadata
                pageUrl={getLandingUrlFromPath(seoData?.url)}
                author={{
                  ...dolDinhLucAuthor,
                  name: detailData?.author,
                }}
                publishedDate={seoData?.createdAt || seoData?.lastModifiedAt}
                blogLength={detailData?.blogLength}
                withShareAction
              />
              <LineDivider directional="horizontal" size={1} space={0} color={colorsV2.neutral20} />
              {detailData?.thumbnail && <BlogPageElementCover cover={detailData?.thumbnail} />}
              <InlineTableOfContent>
                {detailData?.isTocShown && (
                  <LandingSideInfoTableOfContent
                    title="Table of content"
                    content={tocContent.map((eachContent) => {
                      const sectionId = `section-${eachContent.headingIndex + 1}`;

                      return ({
                        id: eachContent.id,
                        render: () => (
                          <ElementTableOfContentHeading
                            type={eachContent.type}
                            order={eachContent.orderIndex + 1}
                            text={eachContent.text}
                            onClick={() => {
                              const containerEl = appScrollRef.current as HTMLElement;
                              scrollToElementId(sectionId, containerEl);
                              window.history.replaceState?.(null, null, `#${sectionId}`);
                            }}
                            active={activeElementId && activeElementId === sectionId}
                            withOrder
                          />
                        )
                      });
                    })}
                  />
                )}
              </InlineTableOfContent>
            </BlogContentTopSection>
            <BlogEditorElementDividerIndicator />
            <BlogPageElementBody bodyValue={bodyValue} mediaInfos={detailData?.mediaInfos} />
          </BlogContentLayout>
        </BlogPageLayoutBody>
      </BodyContent>
      <FooterContent ref={footerContentRef}>
        {Boolean(relatedPosts?.length) && (
          <BlogRelatedPosts pageSeoInfo={pageSeoInfo} relatedPosts={relatedPosts} />
        )}
      </FooterContent>
      <FloatingAction
        className={cl('floating-action', {
          visible: isBlogContentOnScreen && !isBlogTopSectionOnScreen && !isFooterContentOnScreen
        })}
      >
        <LandingBlogFloatingActionTocPopoverLayout
          pageUrl={getLandingUrlFromPath(seoData?.url)}
          renderInfoPopover={({ setPopoverVisible }) => (
            <BlogPromotionPopover onClick={() => setPopoverVisible(false)} />
          )}
          renderTocPopover={({ setPopoverVisible }) => (
            <LandingSideInfoTableOfContentPopover
              title="Table of content"
              content={tocContent.map((eachContent) => ({
                id: eachContent.id,
                render: () => (
                  <ElementTableOfContentHeading
                    type={eachContent.type}
                    text={eachContent.text}
                    onClick={() => {
                      const containerEl = appScrollRef.current as HTMLElement;
                      scrollToElementId(eachContent.id, containerEl);
                      setPopoverVisible(false);
                    }}
                    active={activeElementId && activeElementId === eachContent.id}
                    withOrder
                  />
                )
              }))}
              onClose={() => setPopoverVisible(false)}
            />
          )}
          onClickToTop={() => scrollToTop(appScrollRef.current as HTMLElement)}
          canViewToc={detailData?.isTocShown}
        />
      </FloatingAction>
    </Container>
  );
};
